// Adapter layer for the My Profile page (FEATURE_034 phone change, FEATURE_035 password, FEATURE_040
// withdrawal). Reconciled to the authoritative BE SPEC (03_BE_SPEC_Implementation.md): phone-change is
// request/verify/PUT with a PHONE-KEYED OTP (no sessionId/token passed between steps); password change is
// PUT. To keep the shared PhoneVerificationPanel unchanged, the adapter carries the PHONE through the panel's
// opaque `phoneChangeSessionId` / `phoneChangeToken` slots (on the wire they become {newPhone, otp}).
//
// All calls go through this module; flip VITE_USE_MOCK=false to switch each function from its MOCK branch
// to the httpClient branch.

import { signInWithCustomToken } from 'firebase/auth';

import { httpClient } from '@/api/http-client';
import { auth } from '@/api/firebase';
import { endpoints } from '@/api/endpoints';
import { ApiError, mapAxiosError } from '@/api/error';
import { useAuthStore } from '@/app/store/auth.store';
import { PASSWORD_POLICY_RE } from '@/features/auth/schemas/password-policy';
import type { AccountStatus, PortalRole } from '@/shared/types/auth';

// FEATURE_040 — self-withdraw request submitted from My Profile; stays pending until an admin acts.
export interface WithdrawRequest {
  requestedAt: string; // ISO date (YYYY-MM-DD or full ISO)
}

export interface MyProfileDTO {
  id: number;
  email: string;
  name: string;
  phone: string | null;
  organization: string;
  department: string;
  role: PortalRole;
  joinedAt: string;
  passwordChangedAt: string | null;
	status: AccountStatus;
  withdrawRequest: WithdrawRequest | null;
  // SHCT-991 — practitioner licence number; how the hospital EMR identifies the doctor. Null when empty.
  licenseNo?: string | null;
}

export interface LicenseNoUpdateResponse {
  licenseNo: string | null;
}

// NOTE: `phoneChangeSessionId` / `phoneChangeToken` carry the PHONE in the phone-keyed model (see header).
export interface PhoneChangeSendCodeResponse {
  phoneChangeSessionId: string; // = the new phone
  expiresAt: string; // ISO
}

export interface PhoneChangeVerifyResponse {
  phoneChangeToken: string; // = the new phone
  expiresAt: string; // ISO
}

export interface PhoneChangeCommitResponse {
  phone: string; // normalized "010-XXXX-XXXX"
}

export interface PasswordChangeResponse {
  passwordChangedAt: string; // ISO
  // Fresh Firebase custom token from the BE (FEATURE_035 session continuity). The adapter exchanges it via
  // signInWithCustomToken so the next authed call uses a valid token. Optional: the mock branch omits it.
  accessToken?: string;
}

interface ApiSuccess<T> {
  data: T;
}

const IS_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

const MOCK_LATENCY_MS = 200;
const MOCK_VERIFICATION_CODE = '123456';
const MOCK_CODE_TTL_SEC = 300;

const MOCK_PROFILE_BY_ROLE: Record<PortalRole, MyProfileDTO> = {
  DOCTOR: {
    id: 1001,
    email: 'doctor@uuh.ulsan.kr',
    name: '홍길동',
    phone: '010-2222-3333',
    organization: '울산대학교병원',
    department: '정신건강의학과',
    role: 'DOCTOR',
    joinedAt: '2026-01-15',
    passwordChangedAt: '2026-03-02',
    status: 'ACTIVE',
    withdrawRequest: null,
  },
  NURSE: {
    id: 1002,
    email: 'nurse@uuh.ulsan.kr',
    name: '이간호',
    phone: '010-3333-4444',
    organization: '울산대학교병원',
    department: '정신건강의학과',
    role: 'NURSE',
    joinedAt: '2026-01-20',
    passwordChangedAt: '2026-02-28',
    status: 'ACTIVE',
    withdrawRequest: null,
  },
  CRC: {
    id: 1003,
    email: 'crc@uuh.ulsan.kr',
    name: '박연구',
    phone: '010-4444-5555',
    organization: '울산대학교병원',
    department: '정신건강의학과',
    role: 'CRC',
    joinedAt: '2026-01-25',
    passwordChangedAt: '2026-02-25',
    status: 'ACTIVE',
    withdrawRequest: null,
  },
};

// FEATURE_040 — mock withdrawn-accounts registry. Shared across the my-profile and auth adapters so that:
// (a) the auth adapter can block login for withdrawn accounts (Phase 2), and
// (b) the my-profile adapter can detect approval and trigger forced logout (Phase 3).
// In-memory; lives for the page-load lifetime. Keyed by email (the account ID).
export const mockWithdrawnAccounts = new Set<string>();

/**
 * Simulate an admin approving the self-withdraw request for the given email.
 * Call from browser console: `mockApproveWithdraw('doctor@uuh.ulsan.kr')`
 *
 * Side-effects:
 * 1. Adds the email to mockWithdrawnAccounts (login block — Phase 2).
 * 2. Sets profile.status → 'WITHDRAWN' and clears withdrawRequest on the matching mock profile.
 *
 * @returns A confirmation message indicating the withdrawal was approved
 *          and the user will be logged out in approximately 10 seconds.
 */
export function mockApproveWithdraw(email: string): string {
	mockWithdrawnAccounts.add(email);

	let matchedRole: PortalRole | null = null;
	for (const role of Object.keys(MOCK_PROFILE_BY_ROLE) as PortalRole[]) {
		if (MOCK_PROFILE_BY_ROLE[role].email === email) {
			MOCK_PROFILE_BY_ROLE[role] = {
				...MOCK_PROFILE_BY_ROLE[role],
				status: "WITHDRAWN",
				withdrawRequest: null,
			};
			matchedRole = role;
			break;
		}
	}

	if (!matchedRole) {
		return `⚠️  No mock profile found for email "${email}". Available: ${Object.values(
			MOCK_PROFILE_BY_ROLE,
		)
			.map((p) => p.email)
			.join(", ")}`;
	}

	const roleName = MOCK_PROFILE_BY_ROLE[matchedRole].name;
	return `Withdrawal approved successfully.\nThe user (${roleName}, ${email}) will be logged out automatically in 5 seconds.`;
}

// Expose on window for dev-console / Playwright testing (avoids dynamic-import module isolation).
if (typeof window !== "undefined") {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(window as any).mockApproveWithdraw = mockApproveWithdraw;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(window as any).mockWithdrawnAccounts = mockWithdrawnAccounts;
}

// Mock state — phone-keyed (mirrors the BE). In-memory; lives for the page-load lifetime.
const mockCodeExpiry = new Map<string, number>(); // phone → code expiry
const mockVerified = new Set<string>(); // phones that passed OTP

// Mock password state — current password + last-3 history. Pre-seeded so AUTH_PASSWORD_REUSE can be demoed.
let mockCurrentPassword = 'OldPass1!';
const mockPasswordHistory: string[] = ['OldPass1!', 'PrevPass2@', 'OlderPass3#'];

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const digitsOnly = (value: string): string => value.replace(/\D/g, '');
// Wire-normalize a phone for the BE: strip every non-digit so a hyphenated input (010-1234-5678) and a plain
// one (01012345678) both send the same Korean digits. The BE accepts only the Korean format (^010\d{8}$); the
// app ships Korean-only per the customer requirement (RULE_PHONE_001), no international "+" handling.
const normalizePhone = (value: string): string => digitsOnly(value);
const formatPhoneDigits = (digits: string): string => {
  if (digits.length === 11) return digits.replace(/^(\d{3})(\d{4})(\d{4})$/, '$1-$2-$3');
  if (digits.length === 10) return digits.replace(/^(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3');
  return digits;
};

// ──────────────────────────────────────────────────────────────────────────────
// GET /api/v1/medical/me
// ──────────────────────────────────────────────────────────────────────────────
export async function fetchMe(): Promise<MyProfileDTO> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const role = useAuthStore.getState().role;
    return MOCK_PROFILE_BY_ROLE[role];
  }
  try {
    const { data } = await httpClient.get<ApiSuccess<MyProfileDTO>>(endpoints.medicalStaff.me);
    return data.data;
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// POST /api/v1/medical/me/phone/request  body {newPhone}  → {expiresAt}
// ──────────────────────────────────────────────────────────────────────────────
export async function requestPhoneVerificationCode(
  newPhone: string,
): Promise<PhoneChangeSendCodeResponse> {
  const digits = normalizePhone(newPhone);
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    // Korean numbers must be 10–11 digits (the BE owns the authoritative ^010\d{8}$ validation).
    if (digits.length < 10 || digits.length > 11) {
      throw new ApiError('PHONE_INVALID', '올바른 휴대폰 번호 형식을 입력해주세요. (숫자만 10~11자리)', {
        newPhone: 'invalid',
      });
    }
    const role = useAuthStore.getState().role;
    const currentPhone = MOCK_PROFILE_BY_ROLE[role].phone;
    if (currentPhone && normalizePhone(currentPhone) === digits) {
      throw new ApiError('PHONE_UNCHANGED', '현재 번호와 동일합니다. 다른 번호를 입력해주세요.', {
        newPhone: 'same',
      });
    }
    if (digits === '01088888888') {
      throw new ApiError('PHONE_DUPLICATE', '입력하신 정보로 가입할 수 없습니다. 다시 확인해 주세요.', {
        newPhone: 'registered',
      });
    }
    const expiresAt = new Date(Date.now() + MOCK_CODE_TTL_SEC * 1000).toISOString();
    mockCodeExpiry.set(digits, Date.parse(expiresAt));
    return { phoneChangeSessionId: digits, expiresAt };
  }
  try {
    const { data } = await httpClient.post<ApiSuccess<{ expiresAt: string }>>(
      endpoints.medicalStaff.phoneChangeSendCode,
      { newPhone: digits },
    );
    return { phoneChangeSessionId: digits, expiresAt: data.data.expiresAt };
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// POST /api/v1/medical/me/phone/verify  body {newPhone, otp}  → {phoneVerified}
// `phoneChangeSessionId` from the panel = the new phone.
// ──────────────────────────────────────────────────────────────────────────────
export async function verifyPhoneCode(
  phoneChangeSessionId: string,
  code: string,
): Promise<PhoneChangeVerifyResponse> {
  const digits = normalizePhone(phoneChangeSessionId);
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const expiry = mockCodeExpiry.get(digits);
    if (expiry && Date.now() > expiry) {
      throw new ApiError('OTP_EXPIRED', '인증번호가 만료되었습니다. 다시 인증번호를 받아주세요.', {
        code: 'expired',
      });
    }
    if (code !== MOCK_VERIFICATION_CODE) {
      throw new ApiError('OTP_INVALID', '인증번호가 일치하지 않습니다.', { code: 'mismatch' });
    }
    mockVerified.add(digits);
    const expiresAt = new Date(Date.now() + MOCK_CODE_TTL_SEC * 1000).toISOString();
    return { phoneChangeToken: digits, expiresAt };
  }
  try {
    await httpClient.post<ApiSuccess<{ phoneVerified: boolean }>>(
      endpoints.medicalStaff.phoneChangeVerifyCode,
      { newPhone: digits, otp: code },
    );
    const expiresAt = new Date(Date.now() + MOCK_CODE_TTL_SEC * 1000).toISOString();
    return { phoneChangeToken: digits, expiresAt };
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// PUT /api/v1/medical/me/phone  body {newPhone}  → {phone}
// `phoneChangeToken` from the panel = the verified new phone.
// ──────────────────────────────────────────────────────────────────────────────
export async function updatePhone(
  phoneChangeToken: string,
): Promise<PhoneChangeCommitResponse> {
  const digits = normalizePhone(phoneChangeToken);
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    if (!mockVerified.has(digits)) {
      throw new ApiError('SIGNUP_PHONE_UNVERIFIED', '휴대폰 인증을 완료해주세요.');
    }
    const normalized = formatPhoneDigits(digits);
    const role = useAuthStore.getState().role;
    MOCK_PROFILE_BY_ROLE[role] = { ...MOCK_PROFILE_BY_ROLE[role], phone: normalized };
    mockVerified.delete(digits);
    return { phone: normalized };
  }
  try {
    const { data } = await httpClient.put<ApiSuccess<PhoneChangeCommitResponse>>(
      endpoints.medicalStaff.phoneChange,
      { newPhone: digits },
    );
    return data.data;
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// PUT /api/v1/medical/me/license-no  body {licenseNo}  → {licenseNo}   (SHCT-991)
// Blank clears. Letters/digits/hyphen, 1–20 (the server re-checks; LICENSE_NO_INVALID / LICENSE_NO_DUPLICATE).
// ──────────────────────────────────────────────────────────────────────────────
export async function updateLicenseNo(licenseNo: string): Promise<LicenseNoUpdateResponse> {
  const value = licenseNo.trim();
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    if (value && !/^[A-Za-z0-9-]{1,20}$/.test(value)) {
      throw new ApiError('LICENSE_NO_INVALID', '면허번호는 영문, 숫자, 하이픈(-)만 입력해주세요. (최대 20자)');
    }
    const role = useAuthStore.getState().role;
    MOCK_PROFILE_BY_ROLE[role] = { ...MOCK_PROFILE_BY_ROLE[role], licenseNo: value || null };
    return { licenseNo: value || null };
  }
  try {
    const { data } = await httpClient.put<ApiSuccess<LicenseNoUpdateResponse>>(
      endpoints.medicalStaff.licenseNo,
      { licenseNo: value },
    );
    return data.data;
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// PUT /api/v1/medical/me/password  body {currentPassword, newPassword}
// ──────────────────────────────────────────────────────────────────────────────
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<PasswordChangeResponse> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    if (currentPassword !== mockCurrentPassword) {
      throw new ApiError('AUTH_PASSWORD_MISMATCH', '현재 비밀번호가 일치하지 않습니다.', {
        currentPassword: 'mismatch',
      });
    }
    if (!PASSWORD_POLICY_RE.test(newPassword)) {
      throw new ApiError('AUTH_PASSWORD_POLICY', '영문, 숫자, 특수문자를 혼합한 8자 이상으로 입력해주세요.', {
        newPassword: 'policy',
      });
    }
    // Reusing the current password or any of the last 3 → AUTH_PASSWORD_REUSE (SPEC).
    if (newPassword === currentPassword || mockPasswordHistory.includes(newPassword)) {
      throw new ApiError('AUTH_PASSWORD_REUSE', '최근 3회 사용한 비밀번호는 재사용할 수 없습니다.', {
        newPassword: 'reused',
      });
    }
    mockPasswordHistory.unshift(newPassword);
    while (mockPasswordHistory.length > 3) mockPasswordHistory.pop();
    mockCurrentPassword = newPassword;
    const passwordChangedAt = new Date().toISOString();
    const role = useAuthStore.getState().role;
    MOCK_PROFILE_BY_ROLE[role] = {
      ...MOCK_PROFILE_BY_ROLE[role],
      passwordChangedAt: passwordChangedAt.slice(0, 10),
    };
    return { passwordChangedAt };
  }
  try {
    const { data } = await httpClient.put<ApiSuccess<PasswordChangeResponse>>(
      endpoints.medicalStaff.passwordChange,
      { currentPassword, newPassword },
    );
    // FEATURE_035 session continuity: a backend password change leaves the FE's current Firebase ID token
    // unusable, so the BE returns a fresh custom token — exchange it (like login) so currentUser holds a
    // valid auto-refreshing token BEFORE the my-profile refetch (otherwise that GET /me 401s → logout).
    if (data.data.accessToken) {
      await signInWithCustomToken(auth, data.data.accessToken);
    }
    return data.data;
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// POST /api/v1/medical/me/withdrawal-request  ·  DELETE /api/v1/medical/me/withdrawal-request
// ──────────────────────────────────────────────────────────────────────────────
export async function submitWithdrawRequest(): Promise<WithdrawRequest> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const role = useAuthStore.getState().role;
    if (MOCK_PROFILE_BY_ROLE[role].withdrawRequest) {
      throw new ApiError('WITHDRAW_DUPLICATE', '이미 탈퇴 요청이 접수되었습니다.');
    }
    const requestedAt = new Date().toISOString().slice(0, 10);
    MOCK_PROFILE_BY_ROLE[role] = {
      ...MOCK_PROFILE_BY_ROLE[role],
      withdrawRequest: { requestedAt },
    };
    return { requestedAt };
  }
  try {
    const { data } = await httpClient.post<ApiSuccess<WithdrawRequest>>(
      endpoints.medicalStaff.withdrawRequest,
    );
    return data.data;
  } catch (err) {
    throw mapAxiosError(err);
  }
}

export async function cancelWithdrawRequest(): Promise<void> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const role = useAuthStore.getState().role;
    if (!MOCK_PROFILE_BY_ROLE[role].withdrawRequest) {
      throw new ApiError('WITHDRAW_NONE', '취소할 탈퇴 요청이 없습니다.');
    }
    MOCK_PROFILE_BY_ROLE[role] = {
      ...MOCK_PROFILE_BY_ROLE[role],
      withdrawRequest: null,
    };
    return;
  }
  try {
    await httpClient.delete(endpoints.medicalStaff.withdrawRequest);
  } catch (err) {
    throw mapAxiosError(err);
  }
}
